import React, { useRef, useState } from "react";
import { Alert, Button, Select, Space, Table } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { WorkflowInvoice } from "../../../types/workflowInvoice";
import { formatUnit } from "../../../utils/units";
import { useFeedback } from "../../../utils/feedback";
import {
  linkPurchaseReceipt,
  type PendingReceipt,
} from "../api/invoiceWorkflowApi";

export default function ManualPZInvoiceLink({
  invoice,
  candidates,
  onLinked,
}: {
  invoice: WorkflowInvoice;
  candidates: PendingReceipt[];
  onLinked: () => void;
}) {
  const { t } = useTranslation("invoices");
  const text = (key: string) => t(`workflow.manualReceipt.${key}`);
  const feedback = useFeedback();
  const [selected, setSelected] = useState<string>();
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const requestKey = useRef<string | null>(null);
  const pz = candidates.find((row) => row.id === selected);
  const submit = async () => {
    if (!pz || !invoice.id) return;
    setBusy(true);
    requestKey.current ??= `link-pz-${invoice.id}-${Date.now()}`;
    try {
      await linkPurchaseReceipt(invoice.id, {
        expectedVersion: invoice.version!,
        idempotencyKey: requestKey.current,
        recognitionMonth: dayjs().format("YYYY-MM"),
        pzDocumentId: pz.id,
        expectedPzUpdatedAt: pz.updatedAt,
        mapping: pz.items.map((row) => ({
          pzItemId: row.id,
          purchaseInvoiceItemId: mapping[row.id],
        })),
      });
      setSelected(undefined);
      setMapping({});
      requestKey.current = null;
      feedback.success(text("saved"));
      onLinked();
    } catch (error: any) {
      feedback.error(error?.response?.data?.error || text("failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message={text("title")}
        description={text("hint")}
      />
      <Select
        aria-label={text("select")}
        placeholder={text("select")}
        value={selected}
        disabled={busy}
        style={{ width: "100%" }}
        onChange={(id) => {
          setSelected(id);
          setMapping({});
          requestKey.current = null;
        }}
        options={candidates.map((row) => ({
          value: row.id,
          label: row.pzNumber,
        }))}
      />
      {pz && (
        <>
          <Table
            rowKey="id"
            pagination={false}
            dataSource={pz.items}
            columns={[
              {
                title: text("product"),
                render: (_, row) =>
                  `${row.productName} · ${row.quantity} ${formatUnit(row.unit)}`,
              },
              {
                title: text("invoiceLine"),
                render: (_, row) => (
                  <Select
                    style={{ width: "100%", minWidth: 180 }}
                    aria-label={`${text("invoiceLine")} ${row.productName}`}
                    disabled={busy}
                    value={mapping[row.id]}
                    onChange={(id) => {
                      setMapping((current) => ({ ...current, [row.id]: id }));
                      requestKey.current = null;
                    }}
                    options={invoice.lines
                      .filter(
                        (line) =>
                          line.productVariantId === row.productVariantId,
                      )
                      .map((line) => ({
                        value: line.id!,
                        label: line.productName || text("product"),
                      }))}
                  />
                ),
              },
            ]}
          />
          <Button
            type="primary"
            loading={busy}
            disabled={!pz.items.every((row) => mapping[row.id])}
            onClick={() => void submit()}
          >
            {text("link")}
          </Button>
        </>
      )}
    </Space>
  );
}
